import React from "react";
import { useNavigate } from "react-router-dom";
import { House, Sparkles, CircleCheckBig, Building2, Briefcase, Shirt } from "lucide-react";
import { servicesData } from "../../data/mockData";

const iconMap = {
  House,
  Sparkles,
  CircleCheckBig,
  Building2,
  Briefcase,
  Shirt,
};

// 12-col grid: first row of 3, then rows of 4; odd last card spans full width on tablets
const spanClass = (i, total) => {
  const lastOdd = total % 2 === 1 && i === total - 1 ? "md:col-span-2" : "";
  const wide = total % 4 !== 0 && i < 3;
  return `${lastOdd} ${wide ? "lg:col-span-4" : "lg:col-span-3"}`;
};

/* ── Service Card ── */

const ServiceCard = ({ service, className = "" }) => {
  const navigate = useNavigate();
  const IconComponent = iconMap[service.icon];
  
  const handleClick = () => {
    if (service.url) {
      navigate(service.url);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  
  return (
    <div 
      onClick={handleClick}
      data-testid={`service-card-${service.id}`}
      className={`${className} flex flex-col rounded-2xl border-2 border-slate-100 bg-white shadow-lg overflow-hidden hover:shadow-2xl hover:border-cleanora-primary/30 transition-all duration-300 group cursor-pointer`}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          alt={service.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          src={service.image}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        <div className="absolute bottom-4 left-4">
          <div className="w-14 h-14 bg-gradient-to-br from-cleanora-primary to-cleanora-primary-dark rounded-2xl flex items-center justify-center mb-2 shadow-lg">
            {IconComponent && (
              <IconComponent className="w-7 h-7 text-white" />
            )}
          </div>
        </div>
      </div>
      <div className="p-6 space-y-3">
        <h4 className="text-xl font-bold text-slate-900 group-hover:text-cleanora-primary transition-colors">
          {service.title}
        </h4>
        <p className="text-slate-600 leading-relaxed">{service.description}</p>
        <div className="text-cleanora-primary font-bold text-sm group-hover:underline underline-offset-4 flex items-center gap-2">
          Learn More 
          <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
        </div>
      </div>
    </div>
  );
};

/* ── Main Services Section ── */

const ServicesSection = () => {
  return (
    <section id="services" className="scroll-mt-32 py-24 bg-gradient-to-b from-white to-cleanora-soft-mist">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
            {servicesData.title}
          </h3>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {servicesData.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-8">
          {servicesData.services.map((service, i, all) => (
            <ServiceCard key={service.id} service={service} className={spanClass(i, all.length)} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
